import {useCallback, useEffect, useState} from "react";
import {BreadcrumbsType} from "@/app/components/breadcrumbs/widget";
import {NearbyUsersModel, NewUserModel, UserRequests} from "@/app/api/user";
import {CourtBigModel, CourtsRequests} from "@/app/api/courts";
import {route} from "@/app/routes/AppRoutes";
import {useNavigate, useLocation} from "react-router-dom";
import {
  InvitationModel,
  InvitationRequests,
  InvitationStatusEnum, UpdateInvitationStatusRequestModel,
} from "@/app/api/invitation";
import {ReviewModel, ReviewRequestModel, ReviewsRequests, SetReviewRequestModel} from "@/app/api/reviews";
import {BatchesForView, groupInvitationsToBatches} from "@/app/utils/invitations";
import {ChatRequests} from "@/app/api/chat";
import {SearchParamsKey} from "@/app/utils/use_search_params";

interface ScreenState {
  hasError?: string;
  hasLoading: boolean;
  hasLoadingDelete: boolean;
  hasLoadingAccept: boolean;
  hasLoadingGoToChat: boolean;
  hasLoadingComplete: boolean;
  hasLoadingSendReview: boolean;
  items?: Array<BreadcrumbsType | string>;
  batch?: BatchesForView;
  location?: CourtBigModel;
  reviews?: ReviewModel[];
  users?: NearbyUsersModel[];
  areYouAuthor?: boolean;
  youAccepted?: boolean;
  currentUser?: NewUserModel;
  showReview?: boolean;
  review: string;
  rating: number;
}

export const useInvitationPage = () => {
  const navigate = useNavigate();
  const locationRouter = useLocation();
  const [state, setState] = useState<ScreenState>({
    hasLoading: true,
    hasLoadingDelete: false,
    hasLoadingAccept: false,
    hasLoadingGoToChat: false,
    hasLoadingComplete: false,
    hasLoadingSendReview: false,
    items: [BreadcrumbsType.padel, BreadcrumbsType.createAMatch],
    batch: undefined,
    location: undefined,
    reviews: [],
    users: [],
    areYouAuthor: false,
    youAccepted: false,
    currentUser: undefined,
    showReview: false,
    review: '',
    rating: 0
  });

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  const loadData = useCallback(async (batchId: string) => {
    updateState({hasLoading: true});
    try {
      const invitations = await InvitationRequests.getInvitationsByBatchId(batchId);
      const batches = await groupInvitationsToBatches(invitations);
      const batch = batches[0];
      if (!batch) throw new Error("No invitations found");
      const courtId = batch.anotherInvitations[0].courtId;
      const location = courtId ? await CourtsRequests.getCourt({courtId}) : undefined;
      const reviews = courtId ? await ReviewsRequests.getCourtReview({courtId}) : [];
      const currentUser = await UserRequests.getCurrentUser();

      let areYouAuthor = false;
      const users: NearbyUsersModel[] = batch.anotherInvitations.map((invitation: InvitationModel) => {
        if (invitation.fromUserId === currentUser.user.id) areYouAuthor = true;
        return {
          id: invitation.toUserId,
          username: invitation.toUsername,
          imageLink: invitation.toUserImageUrl
        };
      });
      const youAccepted = batch.anotherInvitations.some(i => i.toUserId === currentUser.user.id && i.status === InvitationStatusEnum.ACCEPTED);

      updateState({batch, location, reviews, users, areYouAuthor, youAccepted, currentUser});
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoading: false});
    }
  }, [updateState]);

  useEffect(() => {
    const searchParams = new URLSearchParams(locationRouter.search);
    const batchId = searchParams.get("batchId");
    if (batchId) loadData(batchId);
  }, [locationRouter.search, loadData]);

  const onDeleteInvitations = useCallback(async () => {
    updateState({hasLoadingDelete: true});
    try {
      if (state.batch?.anotherInvitations) {
        await Promise.all(
          state.batch.anotherInvitations.map((i) =>
            InvitationRequests.deleteInvitation(i.id)
          )
        );
      }
      navigate(route.matches);
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoadingDelete: false});
    }
  }, [navigate, updateState, state.batch]);

  const goToChat = useCallback(async () => {
    const batch = state.batch;
    if (!batch) return;

    updateState({hasLoadingGoToChat: true, hasError: undefined});

    try {
      const firstInvitation = batch.anotherInvitations[0];
      const existingChatId = firstInvitation?.chatId;

      if (existingChatId) {
        navigate(`${route.chats}?${SearchParamsKey.chat}=${existingChatId}`);
        return;
      }

      const userIds = batch.anotherInvitations.map(i => i.toUserId).filter(Boolean);
      if (userIds.length === 0) throw new Error("UserIds is empty");
      if (state.currentUser?.user.id === undefined) throw new Error("UserIds is empty");

      const chat = await ChatRequests.createChat([...userIds, state.currentUser?.user.id]);

      await Promise.all(
        batch.anotherInvitations.map(i =>
          InvitationRequests.changeChatInInvitation({invitationId: i.id, chatId: chat.id})
        )
      );

      navigate(`${route.chats}?${SearchParamsKey.chat}=${chat.id}`);
    } catch (error: any) {
      updateState({hasError: error?.message ?? "Chat error"});
    } finally {
      updateState({hasLoadingGoToChat: false});
    }
  }, [navigate, updateState, state.batch, state.currentUser]);

  const onCompleteInvitations = useCallback(async () => {
    updateState({hasLoadingComplete: true});
    try {
      if (state.batch?.anotherInvitations) {
        await Promise.all(
          state.batch.anotherInvitations.map((i) =>
            InvitationRequests.changeInvitationStatus({
              invitationId: i.id,
              invitationStatus: InvitationStatusEnum.COMPLETED
            })
          )
        );
      }
      navigate(route.matches);
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoadingComplete: false});
    }
  }, [navigate, updateState, state.batch]);

  const onAcceptInvitations = useCallback(async () => {
    updateState({hasLoadingAccept: true});
    try {
      const myInvitation = state.batch?.anotherInvitations.length === 1 && state.batch?.anotherInvitations[0].toUserId === state.batch?.anotherInvitations[0].fromUserId
        ? state.batch?.anotherInvitations[0]
        : state.batch?.anotherInvitations.find((i) => i.toUserId === state.currentUser?.user.id);
      if (!myInvitation) return;
      const newInvitation = await InvitationRequests.changeInvitationStatus({
        invitationId: myInvitation.id,
        invitationStatus: InvitationStatusEnum.ACCEPTED
      });
      loadData(state.batch?.batchId || "");
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoadingAccept: false});
    }
  }, [updateState, state.batch, state.currentUser, loadData]);


  const toggleShowReview = useCallback(() => {
    updateState({showReview: !state.showReview});
  }, [updateState, state.showReview]);

  const onSetReview = useCallback((review: string) => {
    updateState({review});
  }, [updateState]);

  const setRating = useCallback((rating: number) => {
    updateState({rating});
  }, [updateState]);

  const sendReview = useCallback(async () => {
    updateState({hasLoadingSendReview: true});
    if (state.location === undefined) {
      toggleShowReview();
      updateState({hasLoadingSendReview: false});
      return
    }

    try {
      const req: SetReviewRequestModel = {rating: state.rating, comment: state.review};
      await ReviewsRequests.setCourtsReview(req, state.location.id || 0);
      const reqStatus: UpdateInvitationStatusRequestModel = {
        invitationId: (state.batch?.anotherInvitations.length === 1 ? state.batch?.anotherInvitations[0].id : state.batch?.anotherInvitations.find((i) => i.toUserId === state.currentUser?.user.id)?.id) ?? 0,
        invitationStatus: InvitationStatusEnum.RATED
      };
      await InvitationRequests.changeInvitationStatus(reqStatus);
      toggleShowReview();
      navigate(route.matches);
    } catch (error: any) {
      updateState({hasError: error.message});
      toggleShowReview();
    } finally {
      updateState({hasLoadingSendReview: false});
      toggleShowReview();
    }
  }, [navigate, updateState, state.batch, state.rating, state.review]);

  return {
    state,
    onDeleteInvitations,
    goToChat,
    onCompleteInvitations,
    onAcceptInvitations,
    toggleShowReview,
    onSetReview,
    setRating,
    sendReview
  };
};
